/**
 * DDR response loop (Q42): the console's calls to /v1/detection/incidents and
 * the routes around it. Kept beside api.ts (same apiFetch, same errors) so the
 * Incidents screen, the Rules page and the Compliance Breaches page share one
 * typed client.
 */

import { apiFetch, ApiError } from './api';

export type IncidentStatus = 'open' | 'acknowledged' | 'contained' | 'closed';
export type IncidentOutcome = 'true_positive' | 'false_positive' | 'benign';
export type SuppressionScope = 'signature' | 'actor' | 'actor_store';
export type ClockState = 'green' | 'amber' | 'red' | 'overdue' | 'done';

export interface BreachClock {
  key: string;
  label: string;
  regulator: string;
  rule: string;
  startedAt: string;
  dueAt: string;
  hours: number;
  doneAt: string | null;
  doneBy: string | null;
  doneNote: string | null;
  state: ClockState;
  elapsed: number;
  remainingMs: number;
}

export interface IncidentBreach {
  id: string;
  status: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  corePushStatus: string | null;
  clocks: BreachClock[];
}

export interface Incident {
  id: string;
  businessUnitId: string | null;
  actorId: string;
  storeKey: string;
  ruleKey: string;
  status: IncidentStatus;
  severity: 'critical' | 'high' | 'medium' | 'low';
  severityScore: number;
  storeSensitivity: 'sensitive' | 'personal' | 'unclassified';
  worstDecision: string;
  decisionCount: number;
  suppressedCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  assignedTo: string | null;
  assignedBy: string | null;
  assignedAt: string | null;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  closedBy: string | null;
  closedAt: string | null;
  outcome: IncidentOutcome | null;
  outcomeNote: string | null;
  breachIncidentId: string | null;
  breach: IncidentBreach | null;
}

export interface IncidentAction {
  id: string;
  action: string;
  actorId: string;
  requestId: string | null;
  detail: Record<string, unknown>;
  at: string;
}

export interface IncidentDecision {
  id: string;
  requestId: string | null;
  decision: string;
  decidedAt: string;
  suppressionId: string | null;
  explanation: string | null;
  tables: string[];
  principalCount: number;
}

export interface PlaybookStepResult {
  step: { type: string; [k: string]: unknown };
  ok: boolean;
  status: 'done' | 'manual' | 'failed';
  message: string;
  actuator: string;
  evidence?: Record<string, unknown>;
  manual?: { instruction: string; completedBy: string | null; completedAt: string | null; note: string | null };
}

export interface PlaybookRun {
  id: string;
  playbookName: string | null;
  status: 'pending_approval' | 'running' | 'succeeded' | 'awaiting_manual' | 'failed' | 'cancelled';
  requestedBy: string | null;
  approver1: string | null;
  approver2: string | null;
  approvedAt: string | null;
  approvalExpiresAt: string | null;
  incidentId: string | null;
  stepsCompleted: PlaybookStepResult[];
  errorMessage: string | null;
  createdAt: string;
}

export interface IncidentDetail {
  incident: Incident;
  actions: IncidentAction[];
  decisions: IncidentDecision[];
  playbookRuns: PlaybookRun[];
  /** Distinct data principals a playbook's session and block steps would act on (at most 50). */
  playbookPrincipalCount: number;
  breakGlass: Array<Record<string, unknown>>;
  suppressions: Array<Record<string, unknown>>;
  learning: { falsePositivesForRuleAndActor: number; truePositivesForRuleAndActor: number; truePositivesOnStore: number };
}

export interface IncidentCounts {
  open: number;
  acknowledged: number;
  contained: number;
  closed: number;
  withSuppressedHits: number;
}

const post = <T>(path: string, body?: unknown, method: 'POST' | 'DELETE' = 'POST') =>
  apiFetch<T>(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
const enc = encodeURIComponent;

export function listIncidents(opts: { status?: 'active' | 'closed' | 'all'; suppressed?: boolean } = {}) {
  const q = new URLSearchParams({ status: opts.status ?? 'active' });
  if (opts.suppressed) q.set('suppressed', 'true');
  return apiFetch<{ incidents: Incident[]; counts: IncidentCounts }>(`/v1/detection/incidents?${q.toString()}`);
}

export const getIncident = (id: string) => apiFetch<IncidentDetail>(`/v1/detection/incidents/${enc(id)}`);
export const assignIncident = (id: string, assigneeUserId: string | null) => post<{ incident: Incident }>(`/v1/detection/incidents/${enc(id)}/assign`, { assigneeUserId });
export const acknowledgeIncident = (id: string) => post<{ incident: Incident }>(`/v1/detection/incidents/${enc(id)}/acknowledge`, {});
export const suppressIncident = (id: string, input: { scope: SuppressionScope; reason: string; expiresInHours: number }) =>
  post<{ suppression: { id: string; expiresAt: string } }>(`/v1/detection/incidents/${enc(id)}/suppress`, input);
export const runIncidentPlaybook = (id: string, playbook: string) => post<{ run: PlaybookRun }>(`/v1/detection/incidents/${enc(id)}/playbooks`, { playbook });
/** A playbook from the catalogue with no incident (no principals to act on). */
export const runCataloguePlaybook = (name: string) => post<{ run: PlaybookRun }>(`/v1/detection/playbooks/${enc(name)}/run`, {});
export const approvePlaybookRun = (runId: string) => post<{ run: PlaybookRun }>(`/v1/detection/playbook-runs/${enc(runId)}/approve`, {});
export const completeManualStep = (runId: string, index: number, note: string) =>
  post<{ run: PlaybookRun }>(`/v1/detection/playbook-runs/${enc(runId)}/steps/${index}/complete`, { note });
export const startIncidentBreakGlass = (id: string, input: BreakGlassRequest) => post<{ sessionId: string }>(`/v1/detection/incidents/${enc(id)}/break-glass`, input);
export const confirmIncidentBreach = (id: string) => post<{ breachId: string; clocks: BreachClock[]; corePushStatus: string }>(`/v1/detection/incidents/${enc(id)}/confirm-breach`, {});
export const closeIncident = (id: string, outcome: IncidentOutcome, note: string) =>
  post<{ incident: Incident; learning: { falsePositivesForRuleAndActor: number; truePositivesOnStore: number }; offerSuppression?: { scope: SuppressionScope; incidentId: string } }>(
    `/v1/detection/incidents/${enc(id)}/close`,
    { outcome, note },
  );
export const markBreachClockDone = (breachId: string, key: string, note: string) =>
  post<{ clocks: BreachClock[] }>(`/v1/detection/breaches/${enc(breachId)}/clocks/${enc(key)}/done`, { note });

export interface BreakGlassRequest {
  principalActorId: string;
  scopeTables: string[];
  justification: string;
  expiresInMinutes: number;
}
export const requestBreakGlass = (input: BreakGlassRequest) => post<{ sessionId: string }>('/v1/detection/break-glass/activate', input);
export const approveBreakGlassSession = (id: string) => post<{ sessionId: string; approvedBy: string }>(`/v1/detection/break-glass/${enc(id)}/approve`, {});
export const fileBreakGlassFollowUp = (id: string, followUpSummary: string) => post<{ sessionId: string }>(`/v1/detection/break-glass/${enc(id)}/file-summary`, { followUpSummary });

/** The decision routes act on the decision's incident. */
export const acknowledgeDecisionIncident = (reqId: string) => post<{ incident: Incident }>(`/v1/detection/decisions/${enc(reqId)}/acknowledge`, {});
export const suppressDecisionIncident = (reqId: string, input: { scope: SuppressionScope; reason: string; expiresInHours: number }) =>
  post<{ suppression: { id: string; expiresAt: string } }>(`/v1/detection/decisions/${enc(reqId)}/suppress`, input);

export interface BuiltInRuleStatus {
  name: string;
  description: string;
  ruleType: string;
  severity: string;
  industryPackId: string | null;
  status: 'active' | 'needs_data_source';
  needsDataSource: string | null;
  missingFlags: string[];
}
export const listBuiltInRules = () => apiFetch<{ rules: BuiltInRuleStatus[]; counts: { active: number; needsDataSource: number } }>('/v1/detection/rules/built-in');

export interface RuleVersion {
  id: string;
  name: string;
  status: string;
  version: number;
  lineageId: string;
  regoSource: string;
  deprecatedReason: string | null;
  createdAt: string;
}
export const listRuleVersions = (id: string) => apiFetch<{ lineageId: string; versions: RuleVersion[] }>(`/v1/detection/rules/${enc(id)}/versions`);
export const createRuleVersion = (id: string, regoSource: string) => post<{ rule: RuleVersion }>(`/v1/detection/rules/${enc(id)}/versions`, { regoSource });
export const rollbackRule = (id: string, toVersionRuleId: string) => post<{ rule: RuleVersion; note: string }>(`/v1/detection/rules/${enc(id)}/rollback`, { toVersionRuleId });
export const deprecateRule = (id: string, reason: string) => post<{ rule: RuleVersion }>(`/v1/detection/rules/${enc(id)}`, { reason }, 'DELETE');

/**
 * Why an action was refused, in plain words: the problem detail when the
 * controller sent one (same approver, expired, needs every unit, licence),
 * else the error message.
 */
export function refusalText(err: unknown): string {
  if (err instanceof ApiError) {
    const b = err.body && typeof err.body === 'object' ? (err.body as Record<string, unknown>) : null;
    if (err.status === 402) return 'Your organisation is not licensed for Detection, so this cannot be done. Contact support@foctta.com.';
    // The response loop's own refusals carry a code and a plain-words detail;
    // a permission refusal is already turned into plain words by apiFetch.
    if (b && typeof b['code'] === 'string' && typeof b['detail'] === 'string') return b['detail'];
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}
